/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect } from 'react'
import { useOpenCallMeWidget } from '@vp/care-react-c2c'
import { useChatActions, useChat, useEmailActions } from '@vp/contact-channels'
import AnalyticsTracking from '../components/analytics/analytics'

export const useContactUs = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false)
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false)
  const openCallMeWidget = useOpenCallMeWidget()

  // Chat functionality
  const { startChatAction } = useChatActions()
  const { data: chatData } = useChat()

  // Email functionality
  const { createCaseAction } = useEmailActions()

  // Monitor chat status to track if chat is active
  const chatStatus = chatData?.plainData?.chatStatus
  const isChatStarting = startChatAction.isPending
  const isChatActive = chatStatus === 'chatting' || isChatStarting

  // Update local loading state when chat action changes
  useEffect(() => {
    if (isChatStarting) {
      setIsLoading(true)
    } else if (chatStatus === 'chatting') {
      setIsLoading(false) // Chat has started successfully
    } else if (chatStatus === 'online' && isLoading) {
      setIsLoading(false) // Reset loading if chat becomes available again
    }
  }, [isChatStarting, chatStatus, isLoading])

  const handleEmailSubmit = async (emailData: {
    email: string
    firstName: string
    lastName: string
    subject: string
    comment: string
    companyName?: string
    orderNumber?: string
    phoneNumber?: string
    assets?: Array<{ publicUrl?: string; fileName?: string }>
  }): Promise<{ success: boolean; error?: string }> => {
    try {
      setIsEmailSubmitting(true)

      const result = await createCaseAction.mutateAsync({
        caseType: 'email',
        ...emailData,
      })
      AnalyticsTracking.trackEventWithDetails('CARE Case Submitted', {
        category: 'Global Email',
        label: 'Email Initiated',
        pageStage: 'Customer Care',
      })
      return { success: true }
    } catch (error) {
      return { success: false, error: 'Failed to submit email case' }
    } finally {
      setIsEmailSubmitting(false)
    }
  }

  const handleChatClick = async () => {
    if (!chatData?.isAvailable) {
      return
    }

    // Prevent multiple chat attempts if already starting or  active
    if (isChatActive) {
      return
    }

    try {
      setIsLoading(true)
      await startChatAction.mutateAsync({
        timeoutDuration: 15000,
        customEvent: {
          eventName: 'initCAfromExternalSource',
        },
      })
      AnalyticsTracking.trackEventWithDetails('Chat Initiated', {
        category: 'Global Chat',
        label: 'Chat help initiated',
        pageStage: 'Customer Care',
      })
    } catch (error) {
      setIsLoading(false) // Reset loading state on error
    }
  }

  const handlePhoneClick = () => {
    openCallMeWidget()
  }

  const handleEmailClick = () => {
    setIsEmailModalOpen(true)
    AnalyticsTracking.trackEventWithDetails('Pop-Up Viewed', {
      category: 'Pop-Up',
      label: 'Email Initiated',
      pageStage: 'Customer Care',
    })
  }

  const handleEmailModalClose = () => {
    setIsEmailModalOpen(false)
  }

  return {
    handleChatClick,
    handlePhoneClick,
    handleEmailClick,
    handleEmailModalClose,
    handleEmailSubmit,
    isLoading,
    isEmailSubmitting,
    chatData,
    isEmailModalOpen,
    isChatActive,
    chatStatus,
    emailSubmissionStatus: {
      isPending: createCaseAction.isPending,
      isError: createCaseAction.isError,
      error: createCaseAction.error,
      isSuccess: createCaseAction.isSuccess,
      data: createCaseAction.data,
    },
  }
}
